
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  type?: string;
  structuredData?: object;
  lang?: string;
  alternateUrls?: { lang: string; url: string }[];
  image?: string;
  imageAlt?: string;
}

export const SEO = ({
  title = "IntelliStitch - Create Beautiful Knitting Patterns",
  description = "Create beautiful knitting patterns and bring your yarn projects to life with IntelliStitch. Generate custom patterns, track your projects, and join our knitting community.",
  canonical = window.location.href,
  type = "website",
  structuredData,
  lang = "en",
  alternateUrls = [],
  image = "https://intellistitch.ca/images/65c3a101-1333-42fe-a8c5-e64d0a94b609.png",
  imageAlt = "IntelliStitch - Knitting Pattern Generator"
}: SEOProps) => {
  const location = useLocation();
  const siteTitle = title.includes("IntelliStitch") ? title : `${title} | IntelliStitch`;
  
  // Generate breadcrumb data
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbItems = pathSegments.map((segment, index) => {
    const url = `/${pathSegments.slice(0, index + 1).join('/')}`;
    return {
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@id": `https://intellistitch.ca${url}`,
        name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
      }
    };
  });
  
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "IntelliStitch",
    url: "https://intellistitch.ca",
    description: "Create beautiful knitting patterns and bring your yarn projects to life with IntelliStitch.",
    inLanguage: lang,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://intellistitch.ca/stitch-library?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 0,
          item: {
            "@id": "https://intellistitch.ca",
            name: "Home"
          }
        },
        ...breadcrumbItems
      ]
    }
  };
  
  return (
    <Helmet>
      {/* Basic metadata */}
      <html lang={lang} />
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      
      {/* Performance optimizations */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Alternate language versions */}
      {alternateUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      
      {/* OpenGraph metadata */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="IntelliStitch" />
      <meta property="og:locale" content={lang} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      
      {/* Twitter Card metadata */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

